import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from '../../theme/iconify/iconify';

function Members({ users, members, addMember, removeMember }) {
    const [search, setSearch] = React.useState('');
    const [input, setInput] = React.useState('');

    const { t } = useTranslation();

    return (
        <>
            <Stack sx={{ mb: 1 }}>
                <Autocomplete
                    freeSolo
                    value={search}
                    onChange={(event, member) => {
                        if (!member) return;
                        addMember(member._id);

                        //  Reset input fields
                        setSearch('');
                        setInput('');
                    }}
                    inputValue={input}
                    onInputChange={(event, newInputValue) => {
                        setInput(newInputValue);
                    }}
                    options={users?.filter(u => !members?.includes(u._id)) || []}
                    getOptionLabel={(option) =>
                        typeof option === 'string'
                            ? option
                            : `${option.fname} ${option.lname}`
                    }
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id} style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <Stack
                                key={option._id}
                                sx={{
                                    p: 0.5,
                                    display: 'flex',
                                    justifyContent: 'flex-start !important',
                                }}
                            >
                                <Box display='flex' alignItems='center'>
                                    <Iconify
                                        icon='mdi:user-add-outline'
                                        width={18}
                                        sx={{
                                            mx: 2,
                                            color: 'primary.main'
                                        }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.875rem' }}>
                                            {`${option.fname} ${option.lname}`}
                                        </Typography>
                                        <Typography color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                                            {option.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </li>
                    )}
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            id='mISLec_user_group_member_search'
                            placeholder={t('users.modals.groupMembers.inputs.addMembers.label')}
                            variant='outlined'
                            size='small'
                            fullWidth
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton disabled edge='start'>
                                            <Iconify icon='ion:search' />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiInputBase-input': { fontSize: '0.875rem' },
                            }}
                        />
                    }
                />
            </Stack>
            <Stack>
                <Grid container sx={{ maxHeight: '30vh', overflowY: 'auto', scrollbarWidth: 'none' }}>
                    {members?.map(m => (
                        <Grid item xs={12} key={m} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 0.5,
                            mt: 1,
                            '&:hover': {
                                backgroundColor: alpha('#212B36', 0.02),
                            },
                            borderRadius: '8px',
                            //border: '1px solid rgba(33, 43, 54, 0.12)'
                        }}>
                            <Box display='flex' alignItems='center'>
                                <Iconify
                                    icon='prime:user'
                                    width={24}
                                    sx={{
                                        mx: 1,
                                        color: 'primary.main'
                                    }}
                                />

                                <Box>
                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                        {`${users?.find(u => u._id === m)?.fname} ${users?.find(u => u._id === m)?.lname}`}
                                    </Typography>

                                    <Typography color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                                        {users?.find(u => u._id === m)?.email}
                                    </Typography>
                                </Box>
                            </Box>
                            <Tooltip title={t('users.tooltips.removeFromGroup')} arrow>
                                <IconButton
                                    size='small'
                                    onClick={() => { removeMember(m) }}
                                    sx={{ height: 'fit-content', mr: 1, p: 0.75, borderRadius: '8px' }}
                                >
                                    <Iconify icon='eva:trash-outline' width={18} sx={{ color: 'error.main' }} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    ))}
                    {(!members || members.length === 0) &&
                        <Grid container sx={{ mt: 2 }}>
                            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                <Iconify icon='tdesign:usergroup-add-filled' width={36} sx={{ mr: 2 }} />
                                <Grid item xs={12} md={6}>
                                    <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                        {t('users.table.noMembers.title')}
                                    </Typography>
                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                        {t('users.table.noMembers.description')}
                                    </Typography>
                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                        {t('users.table.noMembers.tip')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Stack>
        </>
    );
}

export default Members;